import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton,
  Paper, Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';

import { UserAddOutlined, ReloadOutlined } from '@ant-design/icons';
import SearchField from 'components/SearchField';

import api from 'utils/api';
import { getUser, isSuperAdmin } from 'utils/auth';
import RegisterModal from 'components/RegisterModal';
import PageHeader from 'components/PageHeader';
import ScrollableTableContainer from 'components/table/ScrollableTableContainer';
import TableActionButton from 'components/table/TableActionButton';
import TableActions from 'components/table/TableActions';
import TableStatusChip from 'components/table/TableStatusChip';
import { mutedOutlineChipSx, roleChipSx } from 'components/table/tableChipStyles';
import { useToast } from 'contexts/ToastContext';
import { pluralize } from 'utils/pluralize';

const ADMIN_ROLES = new Set(['SUPER_ADMIN', 'ADMIN', 'FRANCHISE_ADMIN', 'BRANCH_ADMIN']);

function getRowRoleName(user) {
  return (user?.Role?.role_name || user?.role_name || '').toUpperCase();
}

function getRowUserType(user) {
  return (user?.UserType?.type_name || user?.type_name || '').toUpperCase();
}

/** Admin accounts cannot be deleted from the users table. */
function isAdminRowUser(user) {
  return ADMIN_ROLES.has(getRowRoleName(user)) || getRowUserType(user) === 'ADMIN';
}

function canDeleteUser(loggedInUser, rowUser) {
  return isSuperAdmin(loggedInUser) && !isAdminRowUser(rowUser);
}

/** Normalize user-list API payload. */
function parseUsersListResponse(res) {
  const body = res?.data;
  const data = body?.data ?? body;

  const users =
    data?.users ??
    data?.user_list ??
    data?.userList ??
    data?.list ??
    (Array.isArray(data) ? data : []);

  const total =
    data?.total ??
    data?.total_count ??
    data?.totalCount ??
    data?.count ??
    users.length;

  return {
    users: Array.isArray(users) ? users : [],
    total: Number(total) || 0
  };
}

export default function UserList() {
  const loggedInUser = getUser();
  const { showToast, showApiError } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [users, search]);

  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Register Modal
  const [registerModalOpen, setRegisterModalOpen] = useState(false);

  // Fetch Users (Similar to Login style)
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/user-list');

      const ok = res.data?.status !== false;
      if (ok) {
        const { users: list } = parseUsersListResponse(res);
        setUsers(list);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      // 401 handled globally by api interceptor
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Delete Handler (Consistent with Login)
  const handleDeleteClick = (user) => {
    if (!canDeleteUser(loggedInUser, user)) return;
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete || !canDeleteUser(loggedInUser, userToDelete)) return;

    try {
      const res = await api.delete(`/delete-user/${userToDelete.user_id || userToDelete.id}`);

      if (res.data.status) {
        showToast('User deleted successfully', { severity: 'success' });
        fetchUsers(); // Refresh list
      } else {
        showToast(res.data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showApiError(error, 'Error deleting user');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle="Manage all system users and access levels."
        count={filteredUsers.length}
        actionLabel="Register User"
        actionIcon={UserAddOutlined}
        onAction={() => setRegisterModalOpen(true)}
      />

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <SearchField
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, mb: 0, flex: 1, maxWidth: 420 }}
          />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchUsers} size="small">
              <ReloadOutlined />
            </IconButton>
          </Tooltip>
        </Box>

        <ScrollableTableContainer sx={{ maxHeight: 520 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>User Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user, idx) => (
                  <TableRow key={user.user_id || user.id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell><Typography fontWeight={600}>{user.name}</Typography></TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={(user.Role?.role_name || user.role_name || '—').replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                        sx={roleChipSx}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.UserType?.type_name || user.type_name || '—'}
                        size="small"
                        variant="outlined"
                        sx={mutedOutlineChipSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TableStatusChip active={!!user.status} />
                    </TableCell>
                    <TableCell align="center">
                      {canDeleteUser(loggedInUser, user) ? (
                        <TableActions>
                          <TableActionButton
                            action="delete"
                            onClick={() => handleDeleteClick(user)}
                          />
                        </TableActions>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
              {!loading && filteredUsers.length > 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 1.5, border: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      All {filteredUsers.length} {pluralize(filteredUsers.length, 'user')} loaded
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{userToDelete?.name}</strong>?
          <br />This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
      <RegisterModal
        open={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        onSuccess={(newUser) => {
          setRegisterModalOpen(false);
          showToast(`User ${newUser?.name || ''} created successfully`, { severity: 'success' });
          fetchUsers();
        }}
      />
    </Box>
  );
}